import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Target,
  Shield,
  Users,
  CheckCircle2,
  ArrowRight,
  Phone,
  Upload,
  Globe,
  Award,
  TrendingUp,
  FileCheck,
  Briefcase
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export const AboutPage = () => {
  const values = [
    {
      title: 'Accuracy Above All',
      description: 'Regulatory submissions demand precision — and we ensure every document reflects it.',
      icon: Target,
      color: '#606090'
    },
    {
      title: 'Transparency & Clarity',
      description: 'No hidden steps. No surprises. Every stage is clearly communicated to you and your importer.',
      icon: Shield,
      color: '#7090A0'
    },
    {
      title: 'Region-Specific Expertise',
      description: 'Different regions expect different dossier styles. We deliver what regulators actually want.',
      icon: Globe,
      color: '#A060B0'
    },
    {
      title: 'Reliable, Predictable Processes',
      description: 'No last-minute rush. No missing modules. Every submission follows a proven workflow.',
      icon: CheckCircle2,
      color: '#708080'
    }
  ];

  const highlights = [
    'Specialised in Africa, ASEAN, GCC & LATAM regulatory frameworks',
    'Deep expertise across both pharma & nutraceutical categories',
    'Led 100+ dossier submissions for diverse formulations',
    'Strong alignment with importer & MOH communication workflows',
    'Advocate of checklist-driven, accuracy-first processes'
  ];

  const differentiators = [
    {
      title: 'Exclusive regional focus',
      description: 'LATAM, GCC, Africa and Asia',
      icon: Globe
    },
    {
      title: 'Dual capability',
      description: 'Pharma + Nutraceutical regulatory support',
      icon: Award
    },
    {
      title: 'Founder-led execution',
      description: 'Every file reviewed by a senior RA specialist',
      icon: Users
    },
    {
      title: 'Clear workflows',
      description: 'Checklist-driven documents, strong internal QA',
      icon: FileCheck
    },
    {
      title: 'Reduced rework',
      description: 'Our structured approach cuts deficiencies significantly',
      icon: TrendingUp
    },
    {
      title: 'End-to-end capability',
      description: 'From strategy → dossier → artwork → queries → lifecycle',
      icon: CheckCircle2
    }
  ];

  const regions = [
    {
      name: 'Latin America',
      slug: 'latam',
      countries: ['Mexico', 'Colombia', 'Peru', 'Chile', 'Panama', 'Dominican Republic', 'Paraguay', 'Costa Rica', 'Uruguay'],
      color: '#708080'
    },
    {
      name: 'GCC',
      slug: 'gcc',
      countries: ['Saudi Arabia', 'UAE', 'Oman', 'Bahrain', 'Kuwait', 'Qatar'],
      color: '#A060B0'
    },
    {
      name: 'Africa',
      slug: 'africa',
      countries: ['Kenya', 'Nigeria', 'Tanzania', 'Uganda', 'Senegal', 'Benin', 'Togo'],
      color: '#606090'
    },
    {
      name: 'Asia',
      slug: 'asia',
      countries: ['Cambodia', 'Myanmar', 'Nepal', 'Sri Lanka', 'Malaysia', 'Mauritius', 'Bangladesh', 'Bhutan', 'Maldives'],
      color: '#7090A0'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About TheApostrophe | Pharma Regulatory Consultancy Ahmedabad India Africa Asia</title>
        <meta
          name="description"
          content="Founder-led pharma regulatory consultancy based in Ahmedabad, India. Expert in Africa, Asia, GCC, LATAM regulatory frameworks. 30+ countries, 500+ dossiers, 95% first-submission success."
        />
        <meta name="keywords" content="TheApostrophe regulatory consultancy, pharma regulatory consultancy Ahmedabad India, regulatory affairs outsourcing, pharma export consultancy, Africa Asia regulatory expert, founder led regulatory services, Indian pharma manufacturers support" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.theapostrophe.com/about" />
        
        <meta property="og:title" content="About TheApostrophe | Expert Pharma Regulatory Consultancy India" />
        <meta property="og:description" content="Specialized regulatory consultancy for pharma exports. 30+ countries, 500+ dossiers, 95% success rate." />
        <meta property="og:url" content="https://www.theapostrophe.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.theapostrophe.com/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About TheApostrophe | Pharma Regulatory Consultancy" />
        <meta name="twitter:description" content="Specialized regulatory consultancy for pharma exports to emerging markets." />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Why should I choose TheApostrophe over other regulatory consultancies?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "TheApostrophe is exclusively focused on Africa, Asia, GCC, and LATAM markets — regions where most consultancies lack deep expertise. We combine founder-led execution, structured checklists, and proven workflows that reduce deficiencies by up to 60%. Every dossier is reviewed by a senior regulatory specialist."
                }
              },
              {
                "@type": "Question",
                "name": "What is your success rate for first-submission approvals?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our first-submission approval rate is approximately 95% when clients provide complete documentation upfront and follow our submission readiness checklist. Our structured GAP analysis and pre-submission QA significantly reduce rejection risks."
                }
              },
              {
                "@type": "Question",
                "name": "Do you provide ongoing support after product approval?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Our Lifecycle Support Pack provides post-approval support including: renewal management, variation submissions, annual compliance reviews, and handling regulatory updates. We ensure your registrations stay active and compliant across all markets."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#A060B0', color: 'white' }}>
            About Us
          </Badge>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight" style={{ color: '#303040' }}>
            About TheApostrophe
          </h1>
          <p className="text-2xl md:text-3xl mb-12 font-medium" style={{ color: '#606090' }}>
            Your regulatory partner for LATAM, GCC, Africa & Asia — bringing clarity, precision, and confidence to every submission.
          </p>
          <p className="text-xl leading-relaxed" style={{ color: '#708080' }}>
            TheApostrophe was built to simplify global expansion for Indian pharmaceutical and nutraceutical manufacturers.
            Where most exporters struggle with unclear regulations, document gaps, importer coordination, or dossier rejections, we provide a structured, senior-led, and region-focused regulatory pathway that reduces uncertainty and accelerates approval timelines.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#303040' }}>
            Our Story
          </h2>
          <div className="space-y-6 text-lg leading-relaxed" style={{ color: '#708080' }}>
            <p>
              Exporting to emerging international markets shouldn't feel overwhelming — but for many manufacturers, it does. Diverse dossier expectations, shifting documentation requirements, and country-specific nuances often lead to repeated rework and costly delays.
            </p>
            <p className="font-semibold" style={{ color: '#606090' }}>
              TheApostrophe was created to bridge this gap.
            </p>
            <p>
              Founded by an experienced regulatory affairs specialist, we bring deep understanding of regulator expectations, country-by-country requirements, and the documentation challenges faced by Indian manufacturers.
              Our goal is simple: to make global expansion predictable, structured, and submission-ready from Day One.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Promise */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#303040' }}>
              Our Mission & Our Promise
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-2xl rounded-3xl p-10 transform transition-all duration-500 hover:scale-105 hover:-translate-y-2" style={{ backgroundColor: '#606090' }}>
              <CardHeader className="pb-6">
                <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center mb-6 transform transition-transform duration-300 hover:rotate-12">
                  <Target className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-3xl text-white mb-4">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-white/90 leading-relaxed">
                  To empower Indian pharma and nutraceutical companies with clear, reliable, and compliant documentation that meets the expectations of regulators across LATAM, GCC, Africa, and Asia.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-2xl rounded-3xl p-10 transform transition-all duration-500 hover:scale-105 hover:-translate-y-2" style={{ backgroundColor: '#A060B0' }}>
              <CardHeader className="pb-6">
                <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center mb-6 transform transition-transform duration-300 hover:rotate-12">
                  <Shield className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-3xl text-white mb-4">Our Promise</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-white/90 leading-relaxed">
                  We ensure every file, every document, and every communication meets the highest standard of accuracy — so your products move faster from submission to approval.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Founder Profile */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.05)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#303040' }}>
              Meet Our Founder
            </h2>
          </div>
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden" style={{ backgroundColor: 'white' }}>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-12" style={{ backgroundColor: '#A060B010' }}>
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div
                      className="w-32 h-32 rounded-full mx-auto mb-6 flex items-center justify-center text-5xl font-bold"
                      style={{ backgroundColor: '#A060B0', color: 'white' }}
                    >
                      PS
                    </div>
                    <h3 className="text-3xl font-bold mb-2" style={{ color: '#303040' }}>
                      Purva Shah
                    </h3>
                    <p className="text-lg" style={{ color: '#A060B0' }}>
                      Regulatory Affairs Specialist
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-12">
                <p className="text-lg mb-8 leading-relaxed" style={{ color: '#708080' }}>
                  Purva Shah brings years of experience in pharmaceutical and nutraceutical regulatory affairs, with hands-on expertise in CTD/ACTD dossier development, artwork compliance, API/DMF coordination, and handling regulatory queries across multiple international markets.
                </p>
                <p className="text-lg mb-8 leading-relaxed" style={{ color: '#708080' }}>
                  Her focus on structured workflows, clarity in communication, and zero-error documentation forms the foundation of TheApostrophe's approach.
                </p>
                <div className="space-y-3">
                  {highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#A060B0' }} />
                      <span style={{ color: '#708080' }}>{highlight}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="mt-8 px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                  >
                    Talk to Our Regulatory Expert
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, rgba(96, 96, 144, 0.03) 0%, rgba(160, 96, 176, 0.05) 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#606090', color: 'white' }}>
              Our Principles
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Our Values
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#708080' }}>
              The foundation of every document we prepare and every relationship we build
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card
                key={index}
                className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 rounded-2xl p-8 group transform hover:-translate-y-3"
                style={{ backgroundColor: 'white' }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                  style={{ backgroundColor: value.color + '20' }}
                >
                  <value.icon className="h-8 w-8" style={{ color: value.color }} />
                </div>
                <h3 className="text-xl font-bold mb-4 transition-colors duration-300" style={{ color: value.color }}>
                  {value.title}
                </h3>
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  {value.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#A060B0', color: 'white' }}>
              Why Choose Us
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              What Makes Us Different
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#708080' }}>
              Our unique approach to regulatory affairs sets us apart
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentiators.map((item, index) => (
              <Card
                key={index}
                className="border-0 shadow-lg hover:shadow-2xl transition-all duration-500 rounded-2xl p-8 group transform hover:-translate-y-2 hover:scale-102"
                style={{ backgroundColor: 'white', borderLeft: `4px solid #A060B0` }}
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                    style={{ backgroundColor: '#A060B030' }}
                  >
                    <item.icon className="h-7 w-7" style={{ color: '#A060B0' }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3 transition-colors duration-300" style={{ color: '#303040' }}>
                      {item.title}
                    </h3>
                    <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Geographic Focus */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, rgba(160, 96, 176, 0.03) 0%, rgba(112, 128, 128, 0.05) 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#708080', color: 'white' }}>
              Global Coverage
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Our Geographic Focus
            </h2>
            <p className="text-xl max-w-4xl mx-auto" style={{ color: '#708080' }}>
              We specialise in markets where regulatory pathways require clarity, structure, and region-specific documentation. Our expertise spans:
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {regions.map((region, index) => (
              <Link
                key={index}
                to={`/regions/${region.slug}`}
                className="block group"
              >
                <Card
                  className="border-0 shadow-lg hover:shadow-2xl transition-all duration-500 rounded-2xl overflow-hidden transform hover:-translate-y-3 cursor-pointer h-full"
                  style={{ backgroundColor: 'white' }}
                >
                  <div className="h-3 transition-all duration-300 group-hover:h-4" style={{ backgroundColor: region.color }} />
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl flex items-center gap-2 transition-colors duration-300" style={{ color: region.color }}>
                      {region.name}
                      <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {region.countries.slice(0, 6).map((country, idx) => (
                        <Badge
                          key={idx}
                          className="text-xs transition-all duration-300 group-hover:scale-105"
                          style={{
                            backgroundColor: region.color + '15',
                            color: region.color,
                            border: `1px solid ${region.color}30`
                          }}
                        >
                          {country}
                        </Badge>
                      ))}
                      {region.countries.length > 6 && (
                        <Badge
                          className="text-xs"
                          style={{
                            backgroundColor: region.color + '30',
                            color: region.color,
                            border: `1px solid ${region.color}50`
                          }}
                        >
                          +{region.countries.length - 6} more
                        </Badge>
                      )}
                    </div>
                    <p className="mt-4 text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ color: region.color }}>
                      Explore {region.name} →
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/countries">
              <Button
                size="lg"
                className="px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                style={{ backgroundColor: '#606090', color: 'white' }}
              >
                View All Countries We Serve
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              How We Support Your Growth
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              Explore our comprehensive regulatory solutions
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/services">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <div className="w-12 h-12 rounded-xl mb-6 flex items-center justify-center" style={{ backgroundColor: '#60609020' }}>
                    <FileCheck className="h-6 w-6" style={{ color: '#606090' }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#606090' }}>
                    Regulatory Services
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    <strong>CTD/ACTD dossier preparation</strong>, API/DMF documentation, <strong>artwork compliance</strong>, translations, deficiency handling
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#606090' }}>
                    <span className="font-semibold">View Services</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/success-packs">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <div className="w-12 h-12 rounded-xl mb-6 flex items-center justify-center" style={{ backgroundColor: '#A060B020' }}>
                    <Briefcase className="h-6 w-6" style={{ color: '#A060B0' }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#A060B0' }}>
                    Success Packs
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    Packaged solutions from <strong>market selection</strong> to <strong>lifecycle management</strong>. Strategic roadmaps for exporters.
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#A060B0' }}>
                    <span className="font-semibold">Explore Packs</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/countries">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <div className="w-12 h-12 rounded-xl mb-6 flex items-center justify-center" style={{ backgroundColor: '#70908020' }}>
                    <Globe className="h-6 w-6" style={{ color: '#7090A0' }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#7090A0' }}>
                    Country Guides
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    <strong>30+ country-specific</strong> regulatory guides with timelines, requirements, and local authority details
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#7090A0' }}>
                    <span className="font-semibold">Browse Countries</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              Learn more about our approach and expertise
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Why should I choose TheApostrophe over other regulatory consultancies?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  TheApostrophe is <strong>exclusively focused on Africa, Asia, GCC, and LATAM markets</strong> — regions where most consultancies lack deep expertise. We combine <strong>founder-led execution</strong>, structured checklists, and proven workflows that reduce deficiencies by up to 60%. Every dossier is reviewed by a senior regulatory specialist, ensuring accuracy and compliance alignment before submission.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Do you work directly with importers and distributors?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Yes. We coordinate directly with your <strong>local importers or distributors</strong> to ensure submission kits are formatted correctly, documentation is aligned with Ministry of Health expectations, and any pre-filing queries are addressed. This <strong>three-way communication</strong> (manufacturer-importer-consultant) minimizes delays and ensures smooth submissions.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  What is your success rate for first-submission approvals?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Our <strong>first-submission approval rate is approximately 95%</strong> when clients provide complete documentation upfront and follow our submission readiness checklist. When deficiencies do occur, they are typically minor administrative clarifications rather than major technical rejections. Our <strong>structured GAP analysis</strong> and <strong>pre-submission QA</strong> significantly reduce rejection risks.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Can you help us if we have already received a deficiency from a regulatory authority?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Absolutely. Our <strong>deficiency and query handling service</strong> includes: MOH query analysis, technical response drafting, evidence document preparation, and coordination with your importer. We ensure responses are complete, technically sound, and aligned with the authority's expectations. Most deficiency responses we handle result in <strong>approval within 30-60 days</strong>.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Do you provide ongoing support after product approval?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Yes. Our <strong>Lifecycle Support Pack</strong> provides post-approval support including: renewal management, variation submissions (formulation changes, artwork updates, pack size changes), annual compliance reviews, and handling regulatory updates. We ensure your registrations stay active and compliant across all markets with <strong>proactive renewal reminders</strong> and documentation tracking.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-6" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  How long have you been in regulatory affairs?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Our founder, <strong>Purva Shah</strong>, has extensive experience in pharmaceutical and nutraceutical regulatory affairs, specializing in <strong>emerging market registrations</strong>. TheApostrophe has supported <strong>100+ dossier submissions</strong> across Africa, Asia, GCC, and LATAM with deep expertise in CTD/ACTD formats, artwork compliance, and country-specific regulatory nuances.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <Card
            className="border-0 shadow-2xl rounded-3xl p-12 md:p-16 text-center"
            style={{ backgroundColor: '#303040' }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Expand With Clarity. Register With Confidence.
            </h2>
            <p className="text-xl mb-10 text-white/80">
              Let's discuss your target markets and build a structured regulatory roadmap for your products.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
                target="_blank" 
                rel="noopener noreferrer external"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
                }}
              >
                <Button
                  size="lg"
                  className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                  style={{ backgroundColor: '#A060B0', color: 'white' }}
                >
                  Book a Call
                  <Phone className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/contact#send-us-your-details">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 border-white text-white hover:bg-white/10"
                >
                  Upload Your Product List
                  <Upload className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};